import axios from 'axios';
import toast from 'react-hot-toast';

// 1. Definimos la función de limpieza fuera para que sea reutilizable
const cleanPaths = (data) => {
  if (!data) return data;
  if (typeof data === 'string') {
    // Esto cambia /src/assets/ por /images/ en cualquier texto
    return data.replace(/\/src\/assets\//g, '/images/');
  }
  if (Array.isArray(data)) {
    return data.map(cleanPaths);
  }
  if (typeof data === 'object') {
    const newObj = {};
    for (const key in data) {
      newObj[key] = cleanPaths(data[key]);
    }
    return newObj;
  }
  return data;
};

// 2. Normalizamos baseURL para asegurar prefijo /api
const rawBaseURL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';
const normalizeBaseURL = (url) => {
  if (!url) return '/api';
  // Si ya contiene '/api', no duplicar
  if (url.endsWith('/api')) return url;
  if (url.startsWith('/')) return '/api';
  try {
    const parsed = new URL(url);
    if (!parsed.pathname || parsed.pathname === '/') {
      parsed.pathname = '/api';
    }
    if (!parsed.pathname.endsWith('/api')) {
      parsed.pathname += '/api';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return url;
  }
};

// Nota: La llamamos 'apiClient', pero al exportarla como 'default',
// puedes seguir importándola como 'api' en otros archivos si quieres.
const apiClient = axios.create({
  baseURL: normalizeBaseURL(rawBaseURL),
  withCredentials: true, // Añadido por si acaso, como estaba en tu otro archivo
  headers: {
    'Content-Type': 'application/json',
  },
});

let onUnauthorizedLogout = null;

export const setUnauthorizedLogoutHandler = (handler) => {
  onUnauthorizedLogout = typeof handler === 'function' ? handler : null;
};

const normalizeToken = (token) => {
  if (!token || token === 'null' || token === 'undefined') return null;
  const sanitized = String(token).trim().replace(/^"|"$/g, '');
  if (!sanitized || sanitized === 'null' || sanitized === 'undefined') return null;
  if (sanitized.toLowerCase().startsWith('bearer ')) {
    return sanitized.slice(7).trim() || null;
  }
  return sanitized;
};

// 3. Interceptor de Petición (Token)
// 🔐 CRÍTICO: Asegurar que el token se envía en TODOS los requests
apiClient.interceptors.request.use(
  (config) => {
    if (config?.data instanceof FormData && config.headers?.['Content-Type']) {
      delete config.headers['Content-Type'];
    }

    const token = normalizeToken(localStorage.getItem('token'));
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 4. Interceptor de Respuesta (¡AQUÍ SE ARREGLAN LAS IMÁGENES Y ERRORES!)
apiClient.interceptors.response.use(
  (response) => {
    // Antes de que el componente reciba los datos, los limpiamos
    if (response.data) {
      response.data = cleanPaths(response.data);
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const isDev = import.meta.env.DEV;
    const token = normalizeToken(localStorage.getItem('token'));
    const backendMessage = String(error.response?.data?.message || '').toLowerCase();
    const tokenLikelyInvalid = /token|jwt|bearer|authorization|autorizado/.test(backendMessage);
    const shouldForceLogout = token && (status === 401 || (status === 400 && tokenLikelyInvalid));

    if (status === 401 || (status === 400 && tokenLikelyInvalid)) {
      // Silencio total para 401 en producción
      const isInitialVerification = url?.includes('/auth/me') || url?.includes('/notifications') || url?.includes('/feed');
      const isAuthEndpoint = url?.includes('/auth/login') || url?.includes('/auth/register');

      if (!isInitialVerification && isDev) {
        console.log(`[API-ERROR] 🔴 Auth inválida: ${url}`);
      }

      if (shouldForceLogout && !isAuthEndpoint) {
        localStorage.removeItem('token');
        delete apiClient.defaults.headers.common['Authorization'];
        if (onUnauthorizedLogout) {
          onUnauthorizedLogout();
        }
      }
      return Promise.reject(error);
    }

    if (status === 404) {
      if (isDev) {
        console.log(`[API-ERROR] 🟡 404 Recurso no encontrado: ${url}`);
      }
      return Promise.reject(error);
    }

    if (status === 500) {
      console.error(`[API-ERROR] 🔴 500 Error del servidor: ${url}`, error.response?.data?.message);
      toast.error('Error en el servidor');
    }

    if (status === 429) {
      console.warn(`[API-ERROR] 🟠 429 Rate limit: ${url}`);
      toast.error('Demasiadas solicitudes. Espera unos segundos.');
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Tu función de subir CV se queda igual, usando el cliente ya configurado
export const uploadCV = async (file, onUploadProgress) => {
  try {
    const formData = new FormData();
    formData.append('cv', file);
    const response = await apiClient.post('/professional-profile/upload-cv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data.profile.cvUrl;
  } catch (error) {
    toast.error('Error al subir el CV.');
    console.error('Error uploading CV:', error);
    return null;
  }
};

export const uploadIdentityDocument = async (file, onUploadProgress) => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    const response = await apiClient.post('/users/profile/identity-documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  } catch (error) {
    toast.error('Error al subir el documento de identidad.');
    console.error('Error uploading identity document:', error);
    return null;
  }
};